import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import type { InternalDmThread } from '@/domain/types';

// ---------------------------------------------------------------------------
// The Ops comms reply loop (W5 mj, 14ym9bv75mj). When Ops messages someone
// about a worklist row ("message this person" — the studio manager for an
// unpaid artist, the artist for an unconfirmed booking, the sweeper for an
// unclosed link), the DM carries a PERSISTED link attachment back to the row:
// `{ kind: 'link', url: '/ops?item=<id>' }`. From that tag the row derives
// whether the thread is not-messaged / awaiting / replied — surviving reloads
// and devices (the old "sent" flag was modal-local state). `/ops?item=<id>`
// opens that row.
// ---------------------------------------------------------------------------

/** The deep link to an Ops worklist row. */
export function opsItemUrl(itemId: string): string {
  return `/ops?item=${encodeURIComponent(itemId)}`;
}

export type OpsThreadState =
  | { state: 'not-messaged' }
  | { state: 'awaiting'; threadId: string; sentAt: string }
  | {
      state: 'replied';
      threadId: string;
      sentAt: string;
      /** The latest reply's author (person id). */
      authorId: string;
      repliedAt: string;
      /** The latest reply, trimmed to 80 characters. */
      snippet: string;
    };

const SNIPPET_MAX = 80;

/**
 * Pure: the reply state of the Ops DM tagged for `itemId`. The LATEST tagged
 * message anchors it; any later message in that thread from someone other than
 * the tag's author is a reply (the newest one gives the snippet).
 */
export function opsThreadState(itemId: string, dmThreads: Record<string, InternalDmThread>): OpsThreadState {
  const url = opsItemUrl(itemId);
  let anchor: { thread: InternalDmThread; index: number; at: string; authorId: string } | undefined;
  for (const thread of Object.values(dmThreads)) {
    thread.messages.forEach((m, index) => {
      if (!m.attachments?.some((a) => a.kind === 'link' && a.url === url)) return;
      if (!anchor || Date.parse(m.timestamp) > Date.parse(anchor.at)) anchor = { thread, index, at: m.timestamp, authorId: m.authorId };
    });
  }
  if (!anchor) return { state: 'not-messaged' };
  const { thread, index, at, authorId } = anchor;
  const replies = thread.messages.slice(index + 1).filter((m) => m.authorId !== authorId && Date.parse(m.timestamp) >= Date.parse(at));
  const last = replies.at(-1);
  if (!last) return { state: 'awaiting', threadId: thread.id, sentAt: at };
  return {
    state: 'replied',
    threadId: thread.id,
    sentAt: at,
    authorId: last.authorId,
    repliedAt: last.timestamp,
    snippet: last.body.slice(0, SNIPPET_MAX),
  };
}

export interface MessageAboutOpsItemInput {
  itemId: string;
  /** Ops's comms person id (the DM author). */
  fromPersonId: string;
  /** Who is being asked (studio manager / artist / sweeper person id). */
  toPersonId: string;
  body: string;
  at?: string;
}

/**
 * Open (or reuse) the Ops <-> person DM and send `body` tagged with the row's
 * link. The DM thread persists through the internalDmThreads write-through, so
 * the tag (and the reply loop it drives) survives a reload.
 */
export function messageAboutOpsItem(input: MessageAboutOpsItemInput): { threadId: string } {
  const brain = useBrainStore.getState();
  const at = input.at ?? useSimulationStore.getState().clockIso;
  const threadId = brain.openOrCreateDm([input.fromPersonId, input.toPersonId], input.fromPersonId, at);
  brain.sendDmMessage(threadId, input.body, {
    authorId: input.fromPersonId,
    timestamp: at,
    mentions: [input.toPersonId],
    attachments: [{ kind: 'link', url: opsItemUrl(input.itemId), label: 'Ops item' }],
  });
  return { threadId };
}
